import { sortByRecent } from "../../../helpers/sortByRecent.helper";
import { MAX_PASSWORD_EXPIRY_DAYS, SECONDS_PER_DAY } from "../constants";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const normalizeStringList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

export const unwrapUserProfilePayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || {};
  if (Array.isArray(payload)) return payload;

  const dig = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
    if (obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)) {
      return obj.data;
    }
    if (
      obj.result &&
      typeof obj.result === "object" &&
      !Array.isArray(obj.result)
    ) {
      return obj.result;
    }
    if (
      obj.profile &&
      typeof obj.profile === "object" &&
      !Array.isArray(obj.profile)
    ) {
      return obj.profile;
    }
    return null;
  };

  let current = payload;
  for (let i = 0; i < 3; i += 1) {
    const nested = dig(current);
    if (!nested) break;
    current = { ...current, ...nested };
  }
  return current;
};

const readNumeric = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Pull password expiry days from common API shapes / casings. */
export const pickPasswordExpiryInSecs = (raw = {}) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const nestedSources = [
    source,
    source.configuration,
    source.config,
    source.settings,
    source.passwordPolicy,
    source.policy,
  ].filter((item) => item && typeof item === "object" && !Array.isArray(item));

  for (const obj of nestedSources) {
    const direct = firstDefined(
      obj.passwordExpiryInDays,
      obj.passwordExpiryDays,
      obj.expiryInDays,
      obj.passwordExpiryInSecs,
      obj.passwordExpiryInSeconds,
      obj.passwordExpirySecs,
      obj.passwordExpireInSecs,
      obj.passwordExpiry,
      obj.password_expiry_in_secs,
      obj.pwdExpiryInSecs,
      obj.PasswordExpiryInSecs
    );
    const asNumber = readNumeric(direct);
    if (asNumber != null) return asDaysFromApi(asNumber);
  }

  return null;
};

/**
 * API field is named passwordExpiryInSecs but the contract is days
 * (same value as the UI). Convert legacy second values when unambiguous.
 */
const asDaysFromApi = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  if (
    n >= SECONDS_PER_DAY &&
    n % SECONDS_PER_DAY === 0 &&
    n / SECONDS_PER_DAY <= MAX_PASSWORD_EXPIRY_DAYS
  ) {
    return Math.round(n / SECONDS_PER_DAY);
  }
  return n;
};

/** Absolute expiry timestamp from API (ms), when provided. */
export const pickPasswordExpiryAt = (raw = {}) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const nestedSources = [
    source,
    source.configuration,
    source.config,
    source.settings,
    source.passwordPolicy,
    source.policy,
  ].filter((item) => item && typeof item === "object" && !Array.isArray(item));

  for (const obj of nestedSources) {
    const direct = firstDefined(
      obj.passwordExpiryDate,
      obj.passwordExpiryAt,
      obj.expiryDate,
      obj.expiresAt,
      obj.passwordExpiresAt
    );
    if (direct == null || direct === "") continue;
    if (typeof direct === "number") {
      const ms = direct > 1e12 ? direct : direct * 1000;
      if (Number.isFinite(ms)) return ms;
    } else {
      const ms = Date.parse(String(direct));
      if (Number.isFinite(ms)) return ms;
    }
  }
  return null;
};

export const normalizeMfaConfiguration = (raw = {}) => {
  const source =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    enforcement: String(
      firstDefined(source.enforcement, source.mode, source.type, "") || ""
    ).trim(),
    methods: normalizeStringList(
      source.methods || source.method || source.allowedMethods
    ),
    enabled: firstDefined(source.enabled, source.mfaEnabled, null),
  };
};

export const isMfaEnabled = (profile = {}) => {
  const mfa = profile?.mfaConfiguration || profile?.mfaConfig || {};
  const enforcement = String(mfa.enforcement || "")
    .trim()
    .toLowerCase();

  // Prefer API enforcement — do not trust a sticky mfaEnabled boolean.
  if (["disabled", "disable", "off", "none", "false"].includes(enforcement)) {
    return false;
  }
  if (
    ["mandatory", "optional", "enabled", "enable", "on", "true"].includes(
      enforcement
    )
  ) {
    return true;
  }

  if (typeof mfa.enabled === "boolean") return mfa.enabled;
  return false;
};

export const formatMfaStatus = (profile = {}) => {
  const mfa = profile?.mfaConfiguration || profile?.mfaConfig || {};
  const enforcement = String(mfa.enforcement || "").trim();
  if (!enforcement) return "Not enabled";
  return enforcement;
};

export const normalizeUserProfile = (rawInput = {}) => {
  if (typeof rawInput === "string") {
    const profileName = rawInput.trim();
    return {
      id: profileName,
      profileName,
      name: profileName,
      passwordExpiryInSecs: null,
      passwordExpiryInDays: null,
      mfaConfiguration: { enforcement: "", methods: [], enabled: null },
      mfaEnabled: false,
      type: "",
    };
  }

  // Guard: never treat a list array as a single profile.
  if (Array.isArray(rawInput)) {
    return normalizeUserProfile(rawInput[0] || {});
  }

  const raw = unwrapUserProfilePayload(rawInput) || {};
  if (Array.isArray(raw)) {
    return normalizeUserProfile(raw[0] || {});
  }

  const profileName = String(
    firstDefined(raw.profileName, raw.name, raw.profile, "") || ""
  ).trim();
  const recordId = String(firstDefined(raw.id, raw._id, profileName) || "").trim();
  const secs = pickPasswordExpiryInSecs(raw);
  const expiryAtMs = pickPasswordExpiryAt(raw);

  // API list/get use `mfaConfig`; older payloads use `mfaConfiguration`.
  const mfaRaw =
    raw.mfaConfig !== undefined
      ? raw.mfaConfig
      : raw.mfaConfiguration !== undefined
        ? raw.mfaConfiguration
        : raw.mfa;
  const mfaConfiguration = normalizeMfaConfiguration(
    mfaRaw && typeof mfaRaw === "object" ? mfaRaw : {}
  );

  const normalized = {
    id: recordId || profileName,
    profileName,
    name: profileName,
    type: String(firstDefined(raw.type, raw.profileType, "") || "").trim(),
    passwordExpiryInSecs: secs,
    passwordExpiryInDays: secs != null && secs >= 0 ? secs : null,
    passwordExpiryAt: expiryAtMs,
    mfaConfiguration,
    createdAt: firstDefined(
      raw.createdAt,
      raw.created_at,
      raw.creationDate,
      raw.createdDate
    ),
    updatedAt: firstDefined(
      raw.updatedAt,
      raw.updated_at,
      raw.lastModifiedDate,
      raw.lastModified
    ),
  };
  normalized.mfaEnabled = isMfaEnabled(normalized);
  return normalized;
};

export const normalizeUserProfileList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    return payload.map(normalizeUserProfile).filter((item) => item.profileName);
  }

  if (Array.isArray(payload.data)) {
    return payload.data
      .map(normalizeUserProfile)
      .filter((item) => item.profileName);
  }
  if (Array.isArray(payload.result)) {
    return payload.result
      .map(normalizeUserProfile)
      .filter((item) => item.profileName);
  }
  if (Array.isArray(payload.profiles)) {
    return payload.profiles
      .map(normalizeUserProfile)
      .filter((item) => item.profileName);
  }
  if (Array.isArray(payload.userProfiles)) {
    return payload.userProfiles
      .map(normalizeUserProfile)
      .filter((item) => item.profileName);
  }

  const unwrapped = unwrapUserProfilePayload(payload);
  if (Array.isArray(unwrapped)) {
    return unwrapped
      .map(normalizeUserProfile)
      .filter((item) => item.profileName);
  }

  // Map-shaped payload: { mobileUser: {...}, deviceUser: {...} }
  if (unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped)) {
    const keys = Object.keys(unwrapped);
    const looksLikeProfileMap =
      keys.length > 0 &&
      !unwrapped.profileName &&
      unwrapped.passwordExpiryInSecs == null &&
      unwrapped.passwordExpiry == null &&
      !unwrapped.mfaConfiguration &&
      unwrapped.mfaConfig == null &&
      keys.every((key) => {
        const value = unwrapped[key];
        return (
          value == null ||
          typeof value === "string" ||
          (typeof value === "object" && !Array.isArray(value))
        );
      });

    if (looksLikeProfileMap) {
      return keys
        .map((key) => {
          const value = unwrapped[key];
          if (value == null || value === "") {
            return normalizeUserProfile({ profileName: key });
          }
          if (typeof value === "string") {
            return normalizeUserProfile({ profileName: key || value });
          }
          return normalizeUserProfile({
            ...value,
            profileName: value.profileName || key,
          });
        })
        .filter((item) => item.profileName);
    }
  }

  const single = normalizeUserProfile(unwrapped);
  return single.profileName ? [single] : [];
};

export const sortUserProfiles = (list = []) => sortByRecent(list);

export const mergeUserProfileDetail = (current, detail) => {
  const base = normalizeUserProfile(current);
  if (!detail) return base;
  const next = normalizeUserProfile(detail);
  // Prefer the freshest API payload entirely for MFA (do not mix methods across rows).
  const mfaConfiguration = next.mfaConfiguration || {
    enforcement: "",
    methods: [],
    enabled: null,
  };
  return {
    ...base,
    ...next,
    id: next.id || base.id,
    profileName: next.profileName || base.profileName,
    passwordExpiryInSecs:
      next.passwordExpiryInSecs != null
        ? next.passwordExpiryInSecs
        : base.passwordExpiryInSecs,
    passwordExpiryInDays:
      next.passwordExpiryInDays != null
        ? next.passwordExpiryInDays
        : base.passwordExpiryInDays,
    passwordExpiryAt:
      next.passwordExpiryAt != null ? next.passwordExpiryAt : base.passwordExpiryAt,
    mfaConfiguration,
    mfaEnabled: isMfaEnabled({ mfaConfiguration }),
  };
};

export const matchesUserProfileSearch = (profile, { name = "" } = {}) => {
  const query = String(name || "").trim().toLowerCase();
  if (!query) return true;
  return (
    String(profile.profileName || "").toLowerCase().includes(query) ||
    String(profile.type || "").toLowerCase().includes(query)
  );
};

export const formatPasswordExpiryDays = (days) => {
  if (days == null || Number.isNaN(Number(days))) return "";
  const value = Number(days);
  if (value < 0) return "";
  const whole = Math.round(value);
  return `${whole.toLocaleString()} day${whole === 1 ? "" : "s"}`;
};

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/**
 * Show calendar date: API absolute expiry when present,
 * otherwise today + max-age days (policy duration).
 */
export const formatPasswordExpiryDate = (days, expiryAtMs = null) => {
  try {
    if (expiryAtMs != null && Number.isFinite(Number(expiryAtMs))) {
      const absolute = new Date(Number(expiryAtMs));
      if (Number.isNaN(absolute.getTime())) return "—";
      return dateFormatter.format(absolute);
    }
    if (days == null || Number.isNaN(Number(days))) return "—";
    const value = Number(days);
    if (value < 0 || !Number.isFinite(value)) return "—";
    const endMs = Date.now() + value * SECONDS_PER_DAY * 1000;
    if (!Number.isFinite(endMs)) return "—";
    const end = new Date(endMs);
    if (Number.isNaN(end.getTime())) return "—";
    return dateFormatter.format(end);
  } catch {
    return "—";
  }
};

/** @deprecated use formatPasswordExpiryDate for display */
export const formatPasswordExpiry = (seconds) => formatPasswordExpiryDays(seconds) || "—";
